import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { autorun } from 'mobx';

import FirebaseContext from '../../../../../context/FirebaseContext';
import UserContext from '../../../../../context/UserContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import { useInstagramAutomationEnabled } from '../../../../../utils/postHog';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import InstagramUser from '../../../../../Model/InstagramUser';
import InstagramConfiguration from './InstagramConfiguration';
import TextSetterMessaging from './TextSetterMessaging';
import { Container } from './styles';

const InstagramSettings = () => {
  const { userId: coachId } = useContext(UserContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { isAdmin } = useSessionStore();
  const isInstagramAutomationEnabled = useInstagramAutomationEnabled(coachId);
  const isComponentMountedRef = useComponentMounted();
  const [isLoading, setIsLoading] = useState(true);
  const [instaUserCollection, setInstaUserCollection] = useState();
  const [instaUser, setInstaUser] = useState();
  const [secondaryInstaUserCollection, setSecondaryInstaUserCollection] = useState();
  const [secondaryInstaUser, setSecondaryInstaUser] = useState();

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const [instaUserCol, secondaryInstaUserCol] = await Promise.all([
        InstagramUser.getInstaUserByCoachId(coachId),
        InstagramUser.getSecondaryInstaUserByCoachId(coachId),
      ]);
      if (isComponentMountedRef.current) {
        setInstaUserCollection(instaUserCol);
        setSecondaryInstaUserCollection(secondaryInstaUserCol);
        setIsLoading(false);
      }
    };
    if (coachId) {
      init();
    }
  }, [coachId, isComponentMountedRef]);

  useEffect(() => {
    const disposer = autorun(() => {
      const instaUserDoc = instaUserCollection?.hasDocs ? instaUserCollection.docs[0] : undefined;
      setInstaUser(instaUserDoc);
    });
    return () => disposer();
  }, [instaUserCollection]);

  useEffect(() => {
    const disposer = autorun(() => {
      const doc = secondaryInstaUserCollection?.hasDocs ? secondaryInstaUserCollection.docs[0] : undefined;
      setSecondaryInstaUser(doc);
    });
    return () => disposer();
  }, [secondaryInstaUserCollection]);

  const handleUsePrimary = useCallback(async () => {
    await instaUser.updateFields({ secondaryCoachId: coachId });
    await remote(
      'instagram-appSubscription',
      { instagramUserId: instaUser.id, subscribe: true },
      { method: 'POST' },
    );
  }, [instaUser, coachId, remote]);

  return (
    <Container>
      <LoadingOverlay isLoading={isLoading} />
      {!isLoading && (
        <>
          <InstagramConfiguration instaUser={instaUser} />
          {isAdmin && isInstagramAutomationEnabled && !!instaUser && (
            <InstagramConfiguration
              instaUser={secondaryInstaUser}
              isSecondary
              primaryInstaUser={instaUser}
              isSameAsPrimary={!!secondaryInstaUser && secondaryInstaUser.id === instaUser.id}
              onUsePrimary={handleUsePrimary}
              coachId={coachId}
            />
          )}
          <TextSetterMessaging instaUser={instaUser} />
        </>
      )}
    </Container>
  );
};

export default compose(observer)(InstagramSettings);
